import { nanoid } from 'nanoid';
import type {
  Diagram,
  DiagramSortMode,
  Project,
  RevisionSource,
  Role,
  StudioState,
} from './types.ts';
import { MAX_REVISIONS } from './types.ts';
import { makeDiagram, makeProject, makeRevision, seedStore } from './persistence.ts';

function nowIso(): string {
  return new Date().toISOString();
}

function updateProjectTimestamp(projectId: string, projects: Project[]): Project[] {
  return projects.map((project) =>
    project.id === projectId ? { ...project, updatedAt: nowIso() } : project,
  );
}

export type StoreUpdater = (prev: StudioState) => StudioState;

export function patchDiagram(diagramId: string, patch: Partial<Diagram>): StoreUpdater {
  return (prev) => {
    let projectId = '';
    const diagrams = prev.diagrams.map((diagram) => {
      if (diagram.id !== diagramId) return diagram;
      projectId = diagram.projectId;
      return { ...diagram, ...patch, updatedAt: nowIso() };
    });
    return {
      ...prev,
      diagrams,
      projects: projectId ? updateProjectTimestamp(projectId, prev.projects) : prev.projects,
    };
  };
}

export function appendChat(diagramId: string, role: Role, text: string): StoreUpdater {
  return (prev) => {
    let projectId = '';
    const diagrams = prev.diagrams.map((diagram) => {
      if (diagram.id !== diagramId) return diagram;
      projectId = diagram.projectId;
      return {
        ...diagram,
        chat: [...diagram.chat, { id: nanoid(8), role, text, createdAt: nowIso() }],
        updatedAt: nowIso(),
      };
    });
    return {
      ...prev,
      diagrams,
      projects: projectId ? updateProjectTimestamp(projectId, prev.projects) : prev.projects,
    };
  };
}

export function commitDiagramCode(
  diagramId: string,
  code: string,
  source: RevisionSource,
  note: string,
): StoreUpdater {
  const normalized = code.trim();
  return (prev) => {
    let projectId = '';
    const diagrams = prev.diagrams.map((diagram) => {
      if (diagram.id !== diagramId) return diagram;
      projectId = diagram.projectId;
      if (diagram.mermaidCode === normalized && source !== 'restore') return diagram;

      const base = diagram.revisions.slice(0, diagram.revisionCursor + 1);
      const last = base[base.length - 1];
      const nextRevisions =
        last && last.mermaidCode === normalized
          ? base
          : [...base, makeRevision(normalized, source, note)];

      const trimmed = nextRevisions.slice(-MAX_REVISIONS);
      const cursorOffset = nextRevisions.length - trimmed.length;

      return {
        ...diagram,
        mermaidCode: normalized,
        revisions: trimmed,
        revisionCursor: Math.max(0, nextRevisions.length - 1 - cursorOffset),
        updatedAt: nowIso(),
      };
    });
    return {
      ...prev,
      diagrams,
      projects: projectId ? updateProjectTimestamp(projectId, prev.projects) : prev.projects,
    };
  };
}

export function selectProjectUpdater(projectId: string): StoreUpdater {
  return (prev) => {
    const diagrams = prev.diagrams.filter((d) => d.projectId === projectId);
    if (diagrams.length) {
      return { ...prev, selectedProjectId: projectId, selectedDiagramId: diagrams[0].id };
    }
    const created = makeDiagram(projectId, 'Untitled Diagram');
    return {
      ...prev,
      selectedProjectId: projectId,
      selectedDiagramId: created.id,
      diagrams: [created, ...prev.diagrams],
    };
  };
}

export function createProjectUpdater(name: string): StoreUpdater {
  return (prev) => {
    const project = makeProject(name);
    const diagram = makeDiagram(project.id, 'Untitled Diagram');
    return {
      ...prev,
      projects: [project, ...prev.projects],
      diagrams: [diagram, ...prev.diagrams],
      selectedProjectId: project.id,
      selectedDiagramId: diagram.id,
    };
  };
}

export function renameProjectUpdater(projectId: string, name: string): StoreUpdater {
  return (prev) => ({
    ...prev,
    projects: prev.projects.map((p) =>
      p.id === projectId ? { ...p, name, updatedAt: nowIso() } : p,
    ),
  });
}

export function deleteProjectUpdater(projectId: string): StoreUpdater {
  return (prev) => {
    const projects = prev.projects.filter((p) => p.id !== projectId);
    const diagrams = prev.diagrams.filter((d) => d.projectId !== projectId);
    if (!projects.length || !diagrams.length) return seedStore();
    const selectedProjectId = projects[0].id;
    const selectedDiagramId =
      diagrams.find((d) => d.projectId === selectedProjectId)?.id || diagrams[0].id;
    return { ...prev, projects, diagrams, selectedProjectId, selectedDiagramId };
  };
}

export function createDiagramUpdater(projectId: string, title: string): StoreUpdater {
  return (prev) => {
    const diagram = makeDiagram(projectId, title);
    return { ...prev, diagrams: [diagram, ...prev.diagrams], selectedDiagramId: diagram.id };
  };
}

export function deleteDiagramUpdater(diagramId: string): StoreUpdater {
  return (prev) => {
    const target = prev.diagrams.find((d) => d.id === diagramId);
    if (!target) return prev;
    const diagrams = prev.diagrams.filter((d) => d.id !== diagramId);
    if (!diagrams.length) return seedStore();
    const sameProject = diagrams.filter((d) => d.projectId === target.projectId);
    if (!sameProject.length) {
      const replacement = makeDiagram(target.projectId, 'Untitled Diagram');
      return {
        ...prev,
        diagrams: [replacement, ...diagrams],
        selectedProjectId: target.projectId,
        selectedDiagramId: replacement.id,
      };
    }
    return { ...prev, diagrams, selectedDiagramId: sameProject[0].id };
  };
}

export function undoDiagramUpdater(diagramId: string): StoreUpdater {
  return (prev) => {
    let projectId = '';
    const diagrams = prev.diagrams.map((d) => {
      if (d.id !== diagramId || d.revisionCursor <= 0) return d;
      projectId = d.projectId;
      const nextCursor = d.revisionCursor - 1;
      return { ...d, revisionCursor: nextCursor, mermaidCode: d.revisions[nextCursor].mermaidCode, updatedAt: nowIso() };
    });
    return {
      ...prev,
      diagrams,
      projects: projectId ? updateProjectTimestamp(projectId, prev.projects) : prev.projects,
    };
  };
}

export function redoDiagramUpdater(diagramId: string): StoreUpdater {
  return (prev) => {
    let projectId = '';
    const diagrams = prev.diagrams.map((d) => {
      if (d.id !== diagramId || d.revisionCursor >= d.revisions.length - 1) return d;
      projectId = d.projectId;
      const nextCursor = d.revisionCursor + 1;
      return { ...d, revisionCursor: nextCursor, mermaidCode: d.revisions[nextCursor].mermaidCode, updatedAt: nowIso() };
    });
    return {
      ...prev,
      diagrams,
      projects: projectId ? updateProjectTimestamp(projectId, prev.projects) : prev.projects,
    };
  };
}

export function restoreRevisionUpdater(diagramId: string, index: number, revision: { mermaidCode: string }): StoreUpdater {
  return (prev) => {
    let projectId = '';
    const diagrams = prev.diagrams.map((d) => {
      if (d.id !== diagramId) return d;
      projectId = d.projectId;
      return { ...d, revisionCursor: index, mermaidCode: revision.mermaidCode, updatedAt: nowIso() };
    });
    return {
      ...prev,
      diagrams,
      projects: projectId ? updateProjectTimestamp(projectId, prev.projects) : prev.projects,
    };
  };
}

export function updateModelUpdater(model: string): StoreUpdater {
  return (prev) => ({ ...prev, model });
}

export function updateDiagramSortModeUpdater(mode: DiagramSortMode): StoreUpdater {
  return (prev) => ({ ...prev, diagramSortMode: mode });
}

export function togglePinDiagramUpdater(diagramId: string): StoreUpdater {
  return (prev) => ({
    ...prev,
    diagrams: prev.diagrams.map((d) =>
      d.id === diagramId ? { ...d, pinned: !d.pinned } : d,
    ),
  });
}

export function addTagUpdater(diagramId: string, tag: string): StoreUpdater {
  return (prev) => ({
    ...prev,
    diagrams: prev.diagrams.map((d) => {
      if (d.id !== diagramId) return d;
      const tags = d.tags || [];
      if (tags.includes(tag)) return d;
      return { ...d, tags: [...tags, tag] };
    }),
  });
}

export function removeTagUpdater(diagramId: string, tag: string): StoreUpdater {
  return (prev) => ({
    ...prev,
    diagrams: prev.diagrams.map((d) => {
      if (d.id !== diagramId) return d;
      return { ...d, tags: (d.tags || []).filter((t) => t !== tag) };
    }),
  });
}
